import { useMemo } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { collection, query, where, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCollection } from '@/hooks/firestore';
import { useAuth } from '@/stores/auth';
import { cn } from '@/lib/cn';
import { FirstRunTour } from '@/components/onboarding/FirstRunTour';
import { LegalConsentGate } from '@/components/auth/LegalConsentGate';
import { PushRegistration } from '@/components/notifications/PushRegistration';
import { EnablePushBanner } from '@/components/notifications/EnablePushBanner';
import { InstallAppBanner } from '@/components/pwa/InstallAppBanner';
import { BrandMark } from '@/components/brand/BrandMark';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import FeedPage from '@/pages/Feed';

/** Primary buyer tabs only — Studio / Admin live under Me. */
const tabs = [
  { to: '/feed', label: 'Feed' },
  { to: '/discover', label: 'Discover' },
  { to: '/bookings', label: 'Bookings' },
  { to: '/notifications', label: 'Inbox' },
  { to: '/profile', label: 'Me' },
] as const;

export function AppShell() {
  const location = useLocation();
  const onFeed = location.pathname === '/feed';
  const firebaseUser = useAuth((s) => s.firebaseUser);

  const unreadQ = useMemo(() => {
    if (!firebaseUser) return null;
    return query(
      collection(db, 'users', firebaseUser.uid, 'notifications'),
      where('isRead', '==', false),
      limit(10),
    );
  }, [firebaseUser]);
  const { data: unread } = useCollection(unreadQ);
  const unreadLabel = unread.length > 9 ? '9+' : unread.length > 0 ? String(unread.length) : null;

  return (
    <div className="flex h-full flex-col">
      <PushRegistration />
      <InstallAppBanner />
      <EnablePushBanner />
      <FirstRunTour />
      <LegalConsentGate />

      <header className="sticky top-0 z-20 border-b border-makhe-line bg-makhe-bg/85 pt-[env(safe-area-inset-top)] backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-5 py-3 md:px-6 md:py-4">
          <BrandMark to="/feed" size={32} withSlogan />
          <div className="flex items-center gap-3">
            <ThemeToggle />
          </div>
        </div>
        <div className="rule-pattern" aria-hidden />
      </header>

      {/* Feed is mounted only while on /feed, so its Firestore listener and
          <video> elements are torn down when the user browses elsewhere — no
          background video buffering on other routes (mobile-data cost). */}
      <main className={cn('min-h-0 flex-1', onFeed ? 'overflow-hidden' : 'overflow-y-auto')}>
        {onFeed ? <FeedPage isVisible /> : <Outlet />}
      </main>

      <nav
        className="z-30 shrink-0 border-t border-makhe-line bg-makhe-bg/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-sm"
        aria-label="Primary"
      >
        <ul className="mx-auto flex max-w-lg items-stretch justify-between px-1">
          {tabs.map((tab) => (
            <li key={tab.to} className="flex-1">
              <NavLink
                to={tab.to}
                className={({ isActive }) =>
                  cn(
                    'group relative flex flex-col items-center gap-0.5 py-2.5 text-[11px] uppercase tracking-widest transition-colors',
                    isActive ? 'text-makhe-ink' : 'text-makhe-muted hover:text-makhe-ink',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="relative font-display text-[15px] normal-case tracking-tight">
                      {tab.label}
                      {tab.to === '/notifications' && unreadLabel && (
                        <span
                          aria-label={`${unread.length} unread notifications`}
                          className="absolute -right-4 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-makhe-brand px-1 text-[9px] font-sans font-bold not-italic leading-none text-makhe-on"
                        >
                          {unreadLabel}
                        </span>
                      )}
                    </span>
                    <span
                      aria-hidden
                      className={cn(
                        'h-0.5 w-5 rounded-full transition-all',
                        isActive ? 'bg-makhe-ink' : 'bg-transparent group-hover:bg-makhe-line',
                      )}
                    />
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
